import React, { useState, useEffect, useCallback, useMemo } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { useNavigate } from "react-router-dom";

const HTMLCSSEditor = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionData, setQuestionData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [fileContents, setFileContents] = useState({});
  const [activeTab, setActiveTab] = useState('index.html');
  const [isMaximized, setIsMaximized] = useState(false);
  const [activeSection, setActiveSection] = useState('output');
  const [activeOutputTab, setActiveOutputTab] = useState('image');
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [additionalMessage, setAdditionalMessage] = useState('');
  const [testResults, setTestResults] = useState({}); // { [fileName]: boolean[] }
  const [selectedTestCaseIndex, setSelectedTestCaseIndex] = useState(null);

  const isTestingContext = window.location.pathname.includes('/coding-web');

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        if (isTestingContext) {
          const storedQuestions = sessionStorage.getItem('codingQuestions');
          if (storedQuestions) {
            try {
              const qs = JSON.parse(storedQuestions);
              setQuestions(qs);
              const storedIndex = sessionStorage.getItem('currentQuestionIndex');
              const initialIndex = storedIndex ? Math.max(0, Math.min(parseInt(storedIndex) || 0, qs.length - 1)) : 0;
              setCurrentQuestionIndex(initialIndex);
              if (qs.length > 0) {
                const currentQuestion = qs[initialIndex];
                setQuestionData(currentQuestion);
                const fc = {};
                Object.keys(currentQuestion.Code_Validation || {}).forEach((fileName) => {
                  if (fileName === 'index.html') {
                    const ansContent = currentQuestion.Code_Validation[fileName]?.Ans;
                    const defaultTemplate = currentQuestion.Template || currentQuestion.defaulttemplate || '';
                    fc[fileName] = ansContent || defaultTemplate;
                  } else {
                    const ansContent = currentQuestion.Code_Validation[fileName]?.Ans;
                    fc[fileName] = ansContent || '';
                  }
                });
                setFileContents(fc);
                if ((currentQuestion.Tabs || []).length > 0) {
                  setActiveTab(currentQuestion.Tabs[0].name);
                }
              }
            } catch (e) {
            }
          }
          setLoading(false);
          return;
        }
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [isTestingContext]);

  const handleQuestionChange = async (index) => {
    if (index >= 0 && index < questions.length) {
      const question = questions[index];
      setQuestionData(question);
      setCurrentQuestionIndex(index);
      setActiveOutputTab('image');
      sessionStorage.setItem('currentQuestionIndex', String(index));

      const sessionKey = `testing_userCode_${question.Qn_name}`;
      let fc = {};

      Object.keys(question.Code_Validation || {}).forEach((fileName) => {
        if (fileName === 'index.html') {
          const ansContent = question.Code_Validation[fileName]?.Ans;
          const defaultTemplate = question.Template || question.defaulttemplate || '';
          fc[fileName] = ansContent || defaultTemplate;
        } else {
          const ansContent = question.Code_Validation[fileName]?.Ans;
          fc[fileName] = ansContent || '';
        }
      });

      const saved = sessionStorage.getItem(sessionKey);
      if (saved) {
        try {
          const sessionCode = JSON.parse(saved);
          Object.keys(sessionCode).forEach((fileName) => {
            if (fc.hasOwnProperty(fileName)) {
              fc[fileName] = sessionCode[fileName];
            }
          });
        } catch (e) {
        }
      }

      setFileContents(fc);
      if ((question.Tabs || []).length > 0) {
        setActiveTab(question.Tabs[0].name);
      }

      setActiveSection('output');
      setSuccessMessage('');
      setAdditionalMessage('');
      setTestResults({});
      setSelectedTestCaseIndex(null);
    }
  };

  const onChangeFileContent = useCallback((value) => {
    setFileContents((prev) => ({ ...prev, [activeTab]: value }));
    if (questionData) {
      const codeToSave = {};
      Object.keys(fileContents).forEach((fileName) => {
        codeToSave[fileName] = fileContents[fileName] || '';
      });
      codeToSave[activeTab] = value;
      const sessionKey = `testing_userCode_${questionData.Qn_name}`;
      try { sessionStorage.setItem(sessionKey, JSON.stringify(codeToSave)); } catch {}
    }
  }, [activeTab, fileContents, questionData]);

  const handleTabClickWithClear = (tabName) => {
    setSuccessMessage('');
    setAdditionalMessage('');
    setActiveTab(tabName);
  };

  // Basic validation against Code_Validation.structure
  const runValidation = (fileName, code, spec) => {
    const results = [];
    const structure = (spec && spec.structure) || [];
    if (fileName.endsWith('.html')) {
      structure.forEach((rule) => {
        // rule.tag or rule.selector expected; simple contains check
        const tag = rule?.tag;
        if (tag) {
          results.push(new RegExp(`<${tag}(\s|>)`, 'i').test(code));
        } else {
          results.push(false);
        }
      });
    } else if (fileName.endsWith('.css')) {
      structure.forEach((rule) => {
        const selector = rule?.selector;
        if (selector) {
          results.push(code.includes(selector));
        } else {
          results.push(false);
        }
      });
    } else if (fileName.endsWith('.js')) {
      // For JS, look for function/variable names if provided
      structure.forEach((rule) => {
        const name = rule?.name || rule?.function || rule?.variable;
        if (name) {
          results.push(code.includes(name));
        } else {
          results.push(false);
        }
      });
    }
    return results;
  };

  const handleCheckCode = async () => {
    setProcessing(true);
    setActiveSection('output');
    try {
      if (questionData && activeTab) {
        const code = fileContents[activeTab] || '';
        const spec = (questionData.Code_Validation || {})[activeTab] || {};
        const results = runValidation(activeTab, code, spec);
        setTestResults((prev) => ({ ...prev, [activeTab]: results }));
        const passed = results.filter(Boolean).length;
        const total = results.length;
        if (total > 0) {
          setSuccessMessage(passed === total ? 'All tests passed' : `${passed}/${total} tests passed`);
          setAdditionalMessage(total === 0 ? '' : '');
        } else {
          setSuccessMessage('Validation complete');
          setAdditionalMessage('No test structure provided.');
        }
        setSelectedTestCaseIndex(results.length ? 0 : null);
      }
    } finally {
      setProcessing(false);
    }
  };

  const getCodeMirrorExtensions = () => [];
  const getCodeMirrorStyle = () => ({ outline: 'none' });

  const renderEditor = () => {
    const currentContent = (fileContents && fileContents[activeTab]) || '';
    const extensions = getCodeMirrorExtensions();
    return (
      <CodeMirror
        key={`${activeTab}-${currentQuestionIndex}`}
        className="text-xl text-start custom-codemirror"
        value={currentContent}
        height="100%"
        extensions={extensions}
        onChange={onChangeFileContent}
        style={getCodeMirrorStyle()}
      />
    );
  };

  const generateOutputCode = (files) => {
    const html = files['index.html'] || '';
    const css = files['styles.css'] || '';
    const js = files['script.js'] || '';
    return `<!doctype html><html><head><meta charset=\"utf-8\"/><style>${css}</style></head><body>${html}<script>(function(){${js}})();</script></body></html>`;
  };

  const srcCode = useMemo(() => {
    return generateOutputCode(fileContents, questionData?.image_urls);
  }, [fileContents, questionData?.image_urls]);

  useEffect(() => () => { document.body.style.overflow = 'unset'; }, []);

  if (loading) {
    return (
      <div className="container-fluid p-0" style={{ height: "100vh", maxWidth: "100%", overflowX: "hidden", backgroundColor: "#f2eeee" }}>
        <div className="p-3">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0" style={{ height: 'calc(100vh - 70px)', overflowX: "hidden", overflowY: "hidden", backgroundColor: "#f2eeee" }}>
      <div className="p-0 my-0" style={{ backgroundColor: "#F2EEEE", marginRight: '10px' }}>
        <div className="container-fluid p-0 pt-2" style={{ maxWidth: "100%", overflowX: "hidden", overflowY: "auto", backgroundColor: "#f2eeee" }}>
          <div className="row g-2">
            <div className="col-12">
              <div className="" style={{ height: "100vh", overflow: "hidden", padding: '0px 0px 65px 0px' }}>
                <div className="d-flex" style={{ height: '100%', width: '100%' }}>
                  <div className="col-1 lg-8 pb-3" style={{ width: "70px", display: "flex", flexDirection: "column", paddingRight: "15px",overflow:"auto" }}>
                    {questions.map((_, index) => (
                      <button
                        key={index}
                        className="btn rounded-2 mb-2 px-1 mx-auto"
                        style={{
                          width: "50px",
                          height: "50px",
                          backgroundColor: currentQuestionIndex === index ? "#42FF58" : "#FFFFFF",
                          color: "#000",
                          cursor: "pointer",
                          boxShadow: "#888 1px 2px 5px 0px"
                        }}
                        onClick={() => handleQuestionChange(index)}
                      >
                        Q{index + 1}
                      </button>
                    ))}
                  </div>

                  <div className="col-5 lg-8 bg-white" style={{ height: "100%", display: "flex", flexDirection: "column", marginLeft: "-10px",  borderRight: "2px solid #dee2e6" }}>
                    <div className="bg-white" style={{ height: "100%", backgroundColor: "#E5E5E533", display: "flex", flexDirection: "column" }}>
                      <div style={{ height: "50%", display: "flex", flexDirection: "column", borderBottom: "2px solid #dee2e6" }}>
                        <div 
                          className="flex-fill overflow-auto p-3"
                          style={{ 
                            scrollbarWidth: "thin",
                            scrollbarColor: "#c1c1c1 #f1f1f1"
                          }}
                        >
                          <div style={{ marginBottom: "20px" }}>
                            <h4 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px", color: "#333" }}>
                              Problem Statement 
                            </h4>
                            <div 
                              style={{ 
                                whiteSpace: "pre-wrap", 
                                wordBreak: "break-word",
                                fontFamily: "inherit",
                                lineHeight: "1.5",
                                fontSize: "14px",
                                color: "#555"
                              }} 
                              dangerouslySetInnerHTML={{ __html: questionData?.Qn || '' }}
                            />
                          </div>

                          <div>
                            <h4 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "10px", color: "#333" }}>
                              Requirements
                            </h4>
                            <div dangerouslySetInnerHTML={{ __html: questionData?.requirements || 'No requirements specified.' }} />
                          </div>
                        </div>
                      </div>

                      <div style={{ height: "50%", display: "flex", flexDirection: "column" }}>
                        <div className="p-2 d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #e9ecef' }}>
                          <h5 className="m-0" style={{ fontSize: '16px', fontWeight: 600 }}>Expected Output</h5>
                          {(questionData?.image_path || questionData?.video_path) && (
                            <div className="d-flex" style={{ gap: '8px' }}>
                              {questionData?.image_path && (
                                <button className={`btn btn-sm ${activeOutputTab === 'image' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setActiveOutputTab('image')}>Image</button>
                              )}
                              {questionData?.video_path && (
                                <button className={`btn btn-sm ${activeOutputTab === 'video' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setActiveOutputTab('video')}>Video</button>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex-fill overflow-auto p-3 d-flex justify-content-center align-items-start" style={{ scrollbarWidth: 'thin', scrollbarColor: '#c1c1c1 #f1f1f1' }}>
                          {((questionData?.image_path && activeOutputTab === 'image') || (questionData?.image_path && !questionData?.video_path)) && (
                            <img src={questionData.image_path} alt="Expected Output" className="img-fluid" style={{ maxWidth: '100%', height: 'auto', borderRadius: 4 }} />
                          )}
                          {((questionData?.video_path && activeOutputTab === 'video') || (!questionData?.image_path && questionData?.video_path)) && (
                            <video src={questionData.video_path} className="img-fluid" controls style={{ maxWidth: '100%', height: 'auto', borderRadius: 4 }} />
                          )}
                          {!questionData?.image_path && !questionData?.video_path && (
                            <div className="text-muted">No expected output available</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-6 lg-8" style={{ height: "100%", display: "flex", flexDirection: "column", width: '55.1%' }}>
                    <div className="bg-white me-3" style={{ height: "45%", backgroundColor: "#E5E5E533" }}>
                      <div className="border-bottom border-dark p-2 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                          {(questionData?.Tabs || []).map((tab, idx) => (
                            <button
                              key={idx}
                              className={`btn btn-sm ${activeTab === tab.name ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => handleTabClickWithClear(tab.name)}
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                            >
                              {tab.name}
                            </button>
                          ))}
                          <button className="btn btn-sm btn-outline-secondary ms-auto" onClick={() => setIsMaximized(true)}>Expand</button>
                        </div>
                      </div>
                      <div className="col top" style={{ height: `calc(100% - 60px)`, overflowY: 'auto', marginBottom: '10px' }}>
                        {renderEditor()}
                      </div>
                    </div>

                    <div style={{ height: "6%", marginRight: '37px', backgroundColor: "#E5E5E533" }} className="d-flex flex-column justify-content-center me-4 pe-3">
                      <div className="d-flex justify-content-between align-items-center h-100">
                        <div className="d-flex flex-column justify-content-center">
                          {processing ? (
                            <h5 className="m-0">Processing...</h5>
                          ) : (
                            <>
                              {successMessage && <h5 className="m-0 ps-1" style={{ fontSize: '14px' }}>{successMessage}</h5>}
                              {additionalMessage && <p className="m-0 ps-1" style={{ fontSize: '10px' }}>{additionalMessage}</p>}
                            </>
                          )}
                        </div>
                        <div className="d-flex justify-content-end">
                          <button
                            className="btn btn-sm btn-light me-2"
                            style={{ whiteSpace: 'nowrap', fontSize: '12px', minWidth: '70px', boxShadow: '#888 1px 2px 5px 0px', height: '30px' }}
                            onClick={handleCheckCode}
                            disabled={processing}
                          >
                            RUN
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white me-3" style={{ height: "48%", backgroundColor: "#E5E5E533", position: "relative" }}>
                      <div className="p-3" style={{ height: "calc(100% - 10px)", display: "flex", flexDirection: "column" }}>
                        <div className="d-flex mb-3" style={{ flexShrink: 0 }}>
                          <button className={`btn ${activeSection === 'output' ? 'btn-primary' : 'btn-outline-primary'} me-2`} onClick={() => setActiveSection('output')} style={{ fontSize: '12px', padding: '6px 12px' }}>
                            Output
                          </button>
                          <button className={`btn ${activeSection === 'testcases' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setActiveSection('testcases')} style={{ fontSize: '12px', padding: '6px 12px' }}>
                            Test Cases
                          </button>
                        </div>

                        {activeSection === 'output' && (
                          <div style={{ flex: 1, maxHeight: '90%', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
                              {srcCode ? (
                                <iframe
                                  style={{ width: '100%', height: '100%', backgroundColor: 'white', color: 'black', border: 'none', outline: 'none', overflow: 'auto' }}
                                  className="w-full h-full"
                                  srcDoc={srcCode}
                                  title="Output"
                                  sandbox="allow-scripts allow-same-origin"
                                  width="100%"
                                  height="100%"
                                  scrolling="yes"
                                />
                              ) : (
                                <div className="d-flex align-items-center justify-content-center h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                  <div className="text-center text-muted">
                                    <div style={{ fontSize: '14px', opacity: 0.6 }}>Click RUN to see output</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {activeSection === 'testcases' && (
                          <div style={{ flex: 1, maxHeight: '90%', overflow: 'hidden' }}>
                            {testResults[activeTab] && testResults[activeTab].length > 0 ? (
                              <div style={{ height: '100%' }}>
                                <div className="d-flex" style={{ height: '100%' }}>
                                  <div className="border-end" style={{ width: '30%', height: '100%', overflowY: 'auto', padding: '10px', scrollbarWidth: 'thin', scrollbarColor: '#c1c1c1 #f1f1f1' }}>
                                    {testResults[activeTab].map((passed, index) => (
                                      <div
                                        key={index}
                                        className={`p-2 border-bottom cursor-pointer ${selectedTestCaseIndex === index ? 'text-primary' : ''}`}
                                        style={{ fontSize: '12px', cursor: 'pointer', minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '6px 0px', borderRadius: '8px', padding: '8px 12px', backgroundColor: selectedTestCaseIndex === index ? '#f2f2f0' : '#f8f9fa', border: '1px solid #dee2e6', color: selectedTestCaseIndex === index ? '#007bff' : '#212529' }}
                                        onClick={() => setSelectedTestCaseIndex(index)}
                                      >
                                        <span style={{ fontSize: '11px', lineHeight: 1.3 }}>Test Case {index + 1}</span>
                                        {passed ? (
                                          <span className="text-success">✓</span>
                                        ) : (
                                          <span className="text-danger">✗</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                  <div className="px-4 pt-3 pb-3" style={{ width: '70%', height: '100%', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#c1c1c1 #f1f1f1' }}>
                                    {selectedTestCaseIndex !== null && testResults[activeTab][selectedTestCaseIndex] !== undefined && (() => {
                                      const passed = !!testResults[activeTab][selectedTestCaseIndex];
                                      const spec = (questionData?.Code_Validation || {})[activeTab] || {};
                                      const structure = spec.structure || [];
                                      const rule = structure[selectedTestCaseIndex] || {};
                                      const isHTML = activeTab.endsWith('.html');
                                      const isCSS = activeTab.endsWith('.css');
                                      const isJS = activeTab.endsWith('.js');
                                      return (
                                        <div>
                                          <div className="mb-3">
                                            <strong>Status: </strong>
                                            <span className={passed ? 'text-success' : 'text-danger'}>
                                              {passed ? 'Pass' : 'Failed'}
                                            </span>
                                          </div>
                                          {isHTML && (
                                            <>
                                              <div className="mb-2"><strong>Tag:</strong> <span className="text-dark">{rule.tag || '-'}</span></div>
                                              {rule.attributes && (
                                                <div className="mb-2"><strong>Attributes:</strong> <code>{JSON.stringify(rule.attributes)}</code></div>
                                              )}
                                            </>
                                          )}
                                          {isCSS && (
                                            <>
                                              <div className="mb-2"><strong>Selector:</strong> <span className="text-dark">{rule.selector || '-'}</span></div>
                                              {rule.props && (
                                                <div className="mb-2"><strong>Props:</strong> <code>{JSON.stringify(rule.props)}</code></div>
                                              )}
                                            </>
                                          )}
                                          {isJS && (
                                            <>
                                              <div className="mb-2"><strong>Name:</strong> <span className="text-dark">{rule.name || rule.function || rule.variable || '-'}</span></div>
                                            </>
                                          )}
                                          {rule.hint && (
                                            <div className="alert alert-info p-2 mt-2" style={{ fontSize: 12 }}>
                                              <strong>Hint:</strong> {rule.hint}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center text-muted" style={{ padding: '20px' }}>
                                <p>Click RUN to validate your code</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HTMLCSSEditor;

